// ============================================================================
// Annual Increases → "Needs a first increase date". Item #710, 2026-10-20.
//
// Ben's rulings: TCSD-0378 (2 Oct, option B — a finance-only place on this page
// to set the first date), TCSD-0438 (8 Oct, A — the date is pre-filled by
// Bryan's rule, not the plain anniversary; finance can change it before saving)
// and 14 Oct in chat: "Day one of a contract start is the date of the
// anniversary. Hospital HOURLY rates increase annually, so hospitals still have
// an annual kicker. KC office an MAVs have no increase."
//
// The group: every Active building with a yearly increase above zero, no
// increase date and no seeded date waiting — the billing review's own kicker
// test. The pre-fill is the database's annual_increase_date() of the next
// contract anniversary on or after today (nextAnniversaryOnOrAfter). A building
// with no contract start gets no guessed date: the box is empty and the button
// waits for a typed one.
//
// Saving is the existing defer_kicker(): it accepts a building with no date,
// files a "Deferred" annual increase with the fixed first-date note (TCSD-0378's
// accepted wrinkle — no new status word) and sets the date. The building then
// leaves this tab, appears on Upcoming, and its billing review stops asking.
// Finance / executive / superuser only — the gate is in the database.
// ============================================================================

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, EmptyState } from '../../shared/components';
import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import {
  dayAfter,
  firstDateGroup,
  firstDateNote,
  fmtLongDate,
  fmtMoney2,
  isoDate,
  nextAnniversaryOnOrAfter,
  type FirstDateGroup,
} from './lib/annual-increase';

export interface FirstDateRow {
  facility_id: string;
  facility_name: string | null;
  facility_type: string | null;
  monthly_rate: number | null;
  contract_start: string | null;
  /** The building's main cleaner by Home's rule, or "no main cleaner on file". */
  cleaner: string;
  anniversary: string | null;
  /** Bryan's rule on the anniversary, from the database. Null with no contract start. */
  prefill: string | null;
  group: FirstDateGroup;
}

const GROUPS: FirstDateGroup[] = ['New accounts', 'Hospitals', 'Other'];

/**
 * One read of the buildings by the billing review's own predicate, their
 * running main-clean lines and cleaners, then one annual_increase_date() per
 * building that has a contract start. Throws on any failure — the page shows
 * ErrorState and no form, never a row with a guessed date.
 */
export async function loadFirstDateRows(): Promise<FirstDateRow[]> {
  const fac = await supabase
    .from('facilities')
    .select('facility_id, facility_name, facility_type, monthly_rate, contract_start')
    .eq('status', 'Active')
    .gt('annual_kicker_pct', 0)
    .is('next_kicker_date', null)
    .eq('kicker_seed_pending', false)
    .order('facility_name', { ascending: true });
  if (fac.error) throw new Error(fac.error.message);
  const buildings = (fac.data ?? []) as Omit<FirstDateRow, 'cleaner' | 'anniversary' | 'prefill' | 'group'>[];
  if (buildings.length === 0) return [];
  const ids = buildings.map((b) => b.facility_id);

  // Home's rule (get_home_work): the cleaner on the newest Active MAIN_CLEAN line.
  const lines = await supabase
    .from('service_assignments')
    .select('facility_id, provider_id, created_at')
    .in('facility_id', ids)
    .eq('assignment_status', 'Active')
    .eq('service_code', 'MAIN_CLEAN')
    .order('created_at', { ascending: false });
  if (lines.error) throw new Error(lines.error.message);
  const newest = new Map<string, string>();
  for (const l of (lines.data ?? []) as { facility_id: string; provider_id: string | null }[]) {
    if (!newest.has(l.facility_id) && l.provider_id) newest.set(l.facility_id, l.provider_id);
  }
  const providerIds = [...new Set(newest.values())];
  const names = new Map<string, string>();
  if (providerIds.length > 0) {
    const prov = await supabase.from('service_providers').select('provider_id, partner_name').in('provider_id', providerIds);
    if (prov.error) throw new Error(prov.error.message);
    for (const p of (prov.data ?? []) as { provider_id: string; partner_name: string | null }[]) {
      if (p.partner_name) names.set(p.provider_id, p.partner_name);
    }
  }

  const today = isoDate(new Date());
  const rows = await Promise.all(
    buildings.map(async (b) => {
      const anniversary = nextAnniversaryOnOrAfter(b.contract_start, today);
      let prefill: string | null = null;
      if (anniversary) {
        const { data, error } = await supabase.rpc('annual_increase_date', { p_anniversary: anniversary });
        if (error) throw new Error(error.message);
        prefill = (data as string | null) ?? null;
        if (!prefill) throw new Error(`No increase date came back for ${b.facility_name ?? b.facility_id}.`);
      }
      const pid = newest.get(b.facility_id);
      return {
        ...b,
        cleaner: (pid && names.get(pid)) || 'no main cleaner on file',
        anniversary,
        prefill,
        group: firstDateGroup(b.monthly_rate, b.facility_type),
      } satisfies FirstDateRow;
    }),
  );
  return rows.sort(
    (a, b) =>
      GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group) ||
      (a.prefill ?? '9999').localeCompare(b.prefill ?? '9999') ||
      (a.facility_name ?? '').localeCompare(b.facility_name ?? ''),
  );
}

export function FirstIncreaseList({
  rows,
  onSaved,
}: {
  rows: FirstDateRow[];
  onSaved: (row: FirstDateRow, date: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Every building has an increase date"
        body="A building appears here when it has a yearly increase and no date for it yet."
      />
    );
  }
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-st-info-bg px-3 py-2 text-sm text-tcs-text-secondary">
        The anniversary is day one of the contract. The date is filled in by Bryan&apos;s rule from the next
        anniversary: an anniversary on the 1st–7th → the 1st of that month; on the 8th or later → the 1st of the next
        month. Change it if the contract says otherwise, then press <b>Set first date</b>.
      </div>
      {GROUPS.map((g) => {
        const items = rows.filter((r) => r.group === g);
        if (items.length === 0) return null;
        return (
          <section key={g}>
            <h2 className="mb-2 text-sm font-semibold text-tcs-text">
              {g} ({items.length})
            </h2>
            <ul className="space-y-2">
              {items.map((r) => (
                <FirstDateCard key={r.facility_id} row={r} onSaved={onSaved} />
              ))}
            </ul>
          </section>
        );
      })}
      <p className="text-xs text-tcs-text-muted">
        Buildings with no contract start on record show an empty date to type in; nothing is guessed. Where the first
        anniversary has already passed (older hospitals), the next one coming is used. The KC office and MAVS have no
        yearly increase, so they are not listed.
      </p>
    </div>
  );
}

function FirstDateCard({ row, onSaved }: { row: FirstDateRow; onSaved: (row: FirstDateRow, date: string) => void }) {
  const [date, setDate] = useState(row.prefill ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const min = dayAfter(isoDate(new Date()));
  const name = row.facility_name ?? row.facility_id;

  async function save() {
    setBusy(true);
    setError(null);
    const { error: e } = await supabase.rpc('defer_kicker', {
      p_facility_id: row.facility_id,
      p_new_date: date,
      p_notes: firstDateNote(row.prefill, row.anniversary, date),
    });
    setBusy(false);
    if (e) {
      setError(rpcErrorMessage(e, 'Could not save. Try again, and if it persists tell the COO.'));
      return;
    }
    onSaved(row, date);
  }

  return (
    <Card className="p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-tcs-text">
            {name} <span className="font-normal text-tcs-text-secondary">({row.cleaner})</span>
          </div>
          <div className="text-xs text-tcs-text-secondary">
            {row.monthly_rate != null ? `${fmtMoney2(row.monthly_rate)}/mo` : 'billed on actual hours or by department'}
          </div>
          <div className="text-xs text-tcs-text-secondary">
            Contract start (day one): {row.contract_start ? fmtLongDate(row.contract_start) : 'not on record'}
            {' · '}Next anniversary: {row.anniversary ? fmtLongDate(row.anniversary) : 'not on record'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-tcs-text-muted">
            <span>First increase date</span>
            <input
              type="date"
              value={date}
              min={min}
              onChange={(e) => setDate(e.target.value)}
              aria-label={`First increase date for ${name}`}
              className="rounded-md border border-tcs-border bg-tcs-field px-2 py-1 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </label>
          {!row.prefill && !date && (
            <span className="rounded-full bg-st-warn-bg px-2 py-0.5 text-xs text-st-warn-fg-strong">type a date</span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={busy || !date}
            className="inline-flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Set first date
          </button>
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}
    </Card>
  );
}
